import { EditOutlined, UserOutlined } from "@ant-design/icons";
import {
  Avatar,
  Button,
  Drawer,
  Form,
  Row,
  Col,
  Input,
  message,
  Space,
  Tooltip,
  Switch
} from "antd";
import { useEffect, useState } from "react";
import config from "../config/config";
import "./style/Profile.less";
import { edit_profile, upload_profilepic } from "../services/user";
import { useTranslation } from "react-i18next";

// Avatar Component
const ProfileAvatar = ({ src }) => {
  return (
    <Avatar
      size={{
        xs: 40,
        sm: 64,
        md: 80,
        lg: 100,
        xl: 150,
        xxl: 200,
      }}
      src={src}
      icon={<UserOutlined />}
    />
  );
};

// Password validation regex patterns
const PASSWORD_PATTERNS = {
  MIN_LENGTH: /.{8,}/,
  HAS_UPPERCASE: /[A-Z]/,
  HAS_LOWERCASE: /[a-z]/,
};

// Reusable form item styles
const FORM_STYLES = {
  fullWidth: { width: "100%" },
  noMargin: { marginBottom: 8 },
  leftAlign: { textAlign: "left", width: "100%" },
};

const Profile = ({
  userData,
  form,
  isProfileOpen,
  onClose,
  isEdit,
  setIsEdit,
  passwordFormVisible,
  setPasswordFormVisible,
  getProfile,
}) => {
  const reqPic = config.api_backend + "/pic/";
  const { t } = useTranslation();
  
  // State
  const [messageApi, contextHolder] = message.useMessage();
  const [imageUrl, setImageUrl] = useState(userData.profile_img);

  // Update image when userData changes
  useEffect(() => {
    setImageUrl(userData.profile_img);
  }, [userData]);

  // Reset form to initial state
  const resetProfileEdit = () => {
    setIsEdit(false);
    setPasswordFormVisible(false);
    form.resetFields();
  };

  // Success message
  const showSuccess = () => {
    messageApi.open({
      type: "success",
      content: t("Update profile success") || "Update profile success",
    });
  };

  // Error message
  const showError = (content) => {
    messageApi.open({
      type: "error",
      content: content || t("Error cannot edit profile"),
    });
  };

  // Toggle edit mode
  const handleEdit = () => {
    setIsEdit(!isEdit);
  };

  // Form submit handler
  const onFinish = async (values) => {
    const formData = new FormData();

    // Trim all text fields
    const fieldsToTrim = ['name', 'email', 'newpassword', 'username', 'firstname', 'lastname', 'idcard'];
    fieldsToTrim.forEach(field => {
      if (values[field]) {
        values[field] = values[field].trim();
      }
    });

    // Handle profile picture upload
    if (values.profile) {
      formData.append("file", values.profile);
      delete values.profile;
    }

    try {
      const editRes = await edit_profile(values);
      const uploadRes = formData.get("file")
        ? await upload_profilepic(formData)
        : null;

      if (editRes.status === 1) {
        await getProfile();
        setIsEdit(false);
        showSuccess();
      } else {
        showError(editRes.message);
      }
    } catch (error) {
      console.error("Profile update error:", error);
      showError();
    }
  };

  // Password validator
  const validatePassword = (rule, value) => {
    if (!value) {
      return Promise.resolve();
    }

    if (!PASSWORD_PATTERNS.MIN_LENGTH.test(value)) {
      return Promise.reject(
        new Error(t("Password must contain at least 8 characters"))
      );
    }

    if (!PASSWORD_PATTERNS.HAS_UPPERCASE.test(value)) {
      return Promise.reject(
        new Error(t("Password must contain at least one uppercase letter"))
      );
    }

    if (!PASSWORD_PATTERNS.HAS_LOWERCASE.test(value)) {
      return Promise.reject(
        new Error(t("Password must contain at least one lowercase letter"))
      );
    }

    return Promise.resolve();
  };

  // Confirm password validator
  const validateConfirmPassword = ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value || getFieldValue("newpassword") === value) {
        return Promise.resolve();
      }
      return Promise.reject(
        new Error(t("Passwords do not match"))
      );
    },
  });

  const handleSubmit = () => {
    form.submit();
  };

  // Render form field row
  const FormFieldRow = ({ label, name, initialValue, disabled = true, bordered = false }) => (
    <>
      <Col span={8} style={{ display: "flex", alignItems: "center" }}>
        <label style={FORM_STYLES.leftAlign}>
          {label} :
        </label>
      </Col>
      <Col span={16}>
        <Form.Item
          name={name}
          initialValue={initialValue}
          style={FORM_STYLES.noMargin}
        >
          <Input
            className="profile-input"
            bordered={bordered}
            disabled={disabled}
            style={FORM_STYLES.leftAlign}
          />
        </Form.Item>
      </Col>
    </>
  );

  return (
    <Drawer
      className="profile-drawer"
      title={t("Profile")}
      placement="right"
      onClose={onClose}
      open={isProfileOpen}
      style={{ textAlign: "center" }}
    >
      {contextHolder}
      <Space direction="vertical" className="w-full" align="center">
        {/* Form */}
        <Form 
          className="profile-form" 
          onFinish={onFinish} 
          form={form}
          layout="vertical"
        >
          {/* Hidden ID field */}
          <Form.Item
            name="id"
            style={{ display: "none" }}
            initialValue={userData.user_id}
          />

          {/* Profile Picture */}
          <Form.Item className="profile-pic" label="" name="profile">
            <ProfileAvatar src={imageUrl} />
          </Form.Item>

          {/* Username */}
          <Tooltip title={userData.user_name}>
            <Form.Item
              label={isEdit ? t("Username") : ""}
              name="name"
              rules={[
                {
                  required: true,
                  message: t("Please enter your name"),
                },
              ]}
              initialValue={userData.user_name}
            >
              <Input
                className="profile-input"
                bordered={isEdit}
                disabled={!isEdit}
              />
            </Form.Item>
          </Tooltip>

          {/* Form Fields Grid */}
          <Row gutter={[16, 16]} align="middle">
            {/* ID Card */}
            <FormFieldRow
              label={t("ID Card")}
              name="idcard"
              initialValue={userData.id_card}
              disabled={!isEdit}
              bordered={isEdit}
            />

            {/* First Name */}
            <FormFieldRow
              label={t("First Name")}
              name="firstname"
              initialValue={userData.first_name}
              disabled={!isEdit}
              bordered={isEdit}
            />

            {/* Last Name */}
            <FormFieldRow
              label={t("Last Name")}
              name="lastname"
              initialValue={userData.last_name}
              disabled={!isEdit}
              bordered={isEdit}
            />

            {/* Usercode */}
            <FormFieldRow
              label="Usercode"
              name="usercode"
              initialValue={userData.user_code}
              disabled={true}
              bordered={false}
            />

            {/* Email */}
            <FormFieldRow
              label={t("Email")}
              name="email"
              initialValue={userData.user_email}
              disabled={true}
              bordered={false}
            />

            {/* Email Visibility */}
            {isEdit && (
              <>
                <Col span={8} style={{ display: "flex", alignItems: "center" }}>
                  <label style={FORM_STYLES.leftAlign}>
                    {t("Show Email")} :
                  </label>
                </Col>
                <Col span={16} style={{ display: "flex", alignItems: "center" }}>
                  <Form.Item
                    name="status_email"
                    valuePropName="checked"
                    initialValue={userData.status_email === 1}
                    style={FORM_STYLES.noMargin}
                  >
                    <Switch
                      checkedChildren={t("Public")}
                      unCheckedChildren={t("Private")}
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {/* Organization */}
            <FormFieldRow
              label={t("organization")}
              name="org"
              initialValue={userData.org}
              disabled={!isEdit}
              bordered={isEdit}
            />
          </Row>

          {/* Password Fields - Only show in edit mode */}
          {isEdit && (
            <div className="flex flex-col" style={{ marginTop: 20 }}>
              {/* New Password */}
              <Form.Item
                label={t("password")}
                name="newpassword"
                rules={[
                  {
                    required: false,
                    message: t("Please enter your password"),
                  },
                  {
                    validator: validatePassword,
                  },
                ]}
              >
                <Input.Password
                  className="profile-text"
                  placeholder={t("Input your password")}
                />
              </Form.Item>

              {/* Confirm Password */}
              <Form.Item
                label={t("confirm password")}
                name="confirm"
                dependencies={['newpassword']}
                rules={[
                  {
                    required: false,
                    message: t("Please confirm your password"),
                  },
                  validateConfirmPassword,
                ]}
              >
                <Input.Password
                  className="profile-text"
                  placeholder={t("Confirm your new password")}
                />
              </Form.Item>

              {/* Password Requirements */}
              <div style={{ marginBottom: 16, textAlign: "left", fontSize: 12, color: "#666" }}>
                <div>{t("Password requirements")}:</div>
                <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                  <li>{t("At least 8 characters")}</li>
                  <li>{t("At least one uppercase letter (A-Z)")}</li>
                  <li>{t("At least one lowercase letter (a-z)")}</li>
                </ul>
              </div>
            </div>
          )}
        </Form>

        {/* Action Buttons - Only show in edit mode */}
        {isEdit && (
          <div className="profile-button-container">
            <Button
              className="profile-button cancel"
              onClick={resetProfileEdit}
            >
              {t("cancel")}
            </Button>
            <Button
              type="primary"
              className="profile-button"
              onClick={handleSubmit}
            >
              {t("save")}
            </Button>
          </div>
        )}
      </Space>

      {/* Edit Button - Only show when not in edit mode */}
      {!isEdit && (
        <div className="edit-btn-wrapper">
          <Button
            shape="circle"
            icon={<EditOutlined className="profile-edit-icon" />}
            onClick={handleEdit}
          />
        </div>
      )}
    </Drawer>
  );
};

export default Profile;